/**
 * Hero/brand → icon decoration for Companion prose (best practice: the FE owns
 * the icon vocabulary, NOT the LLM).
 *
 * Shared by BOTH web portals: the Protector talks about CheckUp, QultureUp and
 * Care as much as the citizen Companion does, so its replies get the same
 * iconography. Living here (rather than in journey-web) is what stops the two
 * conversations from LOOKING different. The model naturally bolds product names
 * ("**CheckUp**", "**TelePortal**"); we render the matching hero icon inline so
 * the conversation carries our iconography without feeding an icon catalog to
 * the model (no token cost, no hallucinated icon names, always valid).
 */

import { HeroIcon, type HeroType } from '@tonnie/assets';
import { Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

/** Exact brand token → hero icon key (checked case-insensitively). */
const BRAND_TO_HERO: Record<string, HeroType> = {
  checkup: 'checkup',
  teleportal: 'teleportal',
  qultureup: 'qultureup',
  care: 'care',
  passport: 'passport',
};

/** Brands that use the CoPilot sparkle rather than a hero icon. */
const SPARKLE_BRANDS = new Set(['tonnie', 'copilot']);

/**
 * Decorate a **bold** run: if its text is exactly a known brand/hero name,
 * return an inline icon + label; otherwise null (keep the default bold render).
 * Passed to the shared `<Markdown decorateStrong={…}>`.
 */
export function decorateBrand(text: string): ReactNode | null {
  const key = text.trim().toLowerCase();
  const hero = BRAND_TO_HERO[key];
  if (hero) {
    return (
      <span className="inline-flex items-center gap-1 font-semibold align-baseline">
        <HeroIcon hero={hero} className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {text}
      </span>
    );
  }
  if (SPARKLE_BRANDS.has(key)) {
    return (
      <span className="inline-flex items-center gap-1 font-semibold align-baseline">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary-accessible" aria-hidden="true" />
        {text}
      </span>
    );
  }
  return null;
}
